import React, { useState, useEffect, useRef } from 'react';
import './RadiologyTechnicianStudyModal.css';

/**
 * RadiologyTechnicianStudyModal
 * Fast, keyboard-friendly (Tab / Enter / Arrow keys) modal for Radiology Technicians
 * to capture study acquisition protocol, radiation dose/technique parameters,
 * contrast administration, structured measurements, and handover to radiologist.
 */
export default function RadiologyTechnicianStudyModal({
  isOpen,
  onClose,
  testId,
  orderInfo = {},
  initialData = {},
  onSaveAndHandover
}) {
  const [protocolName, setProtocolName] = useState('');
  const [kvMas, setKvMas] = useState('');
  const [contrastDetails, setContrastDetails] = useState('');
  const [measurements, setMeasurements] = useState('');
  const [technicianNotes, setTechnicianNotes] = useState('');
  const [handoverRemarks, setHandoverRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const protocolInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setProtocolName(initialData.protocolName || '');
      setKvMas(initialData.kvMas || '');
      setContrastDetails(initialData.contrastDetails || '');
      setMeasurements(initialData.technicianMeasurements || '');
      setTechnicianNotes(initialData.technicianNotes || '');
      setHandoverRemarks('');
      setErrorMsg(null);
      setTimeout(() => {
        if (protocolInputRef.current) {
          protocolInputRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  // Preset protocols based on investigation modality
  const handleQuickProtocol = (proto) => {
    setProtocolName(proto);
  };

  const handleQuickMeasurement = (label, val) => {
    const entry = `${label}: ${val} cm\n`;
    setMeasurements(prev => (prev ? prev + entry : entry));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setSubmitting(true);
    setErrorMsg(null);
    try {
      await onSaveAndHandover({
        protocolName,
        kvMas,
        contrastDetails,
        technicianMeasurements: measurements,
        technicianNotes,
        status: 'In Review',
        handoverRemarks
      });
      onClose();
    } catch (err) {
      setErrorMsg(err.message || 'Failed to save technician study details.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rad-modal-overlay">
      <div className="rad-tech-modal">
        {/* Header */}
        <div className="rad-tech-header">
          <div>
            <h2>
              <span>🔬</span> Technician Study Acquisition & Handover
            </h2>
            <p>
              Order <strong>#{testId}</strong> &bull; {orderInfo.patientName} &bull; {orderInfo.itemName} ({orderInfo.department || 'Radiology'})
            </p>
          </div>
          <button className="rad-modal-close" onClick={onClose} title="Close (Esc)">✕</button>
        </div>

        {errorMsg && (
          <div className="rad-tech-error">
            ⚠️ {errorMsg}
          </div>
        )}

        <form onSubmit={handleSubmit} className="rad-tech-form">
          {/* Quick Info Bar */}
          <div className="rad-tech-order-bar">
            <div><strong>UHID:</strong> {orderInfo.uhid}</div>
            <div><strong>Lab No:</strong> {orderInfo.labNo}</div>
            <div><strong>Age/Gender:</strong> {orderInfo.ageString || 'Adult'} / {orderInfo.gender}</div>
            <div><strong>Priority:</strong> <span className={`rad-priority-tag ${(orderInfo.priority || 'Routine').toLowerCase()}`}>{orderInfo.priority || 'Routine'}</span></div>
          </div>

          <div className="rad-tech-grid">
            {/* Left Column: Acquisition Parameters */}
            <div className="rad-tech-col">
              <div className="rad-tech-field">
                <label>
                  Protocol / Scan Sequence <span className="req">*</span>
                </label>
                <input
                  ref={protocolInputRef}
                  type="text"
                  placeholder="e.g. Spiral Contrast Enhanced CT, Multiplanar Spine, Chest PA"
                  value={protocolName}
                  onChange={e => setProtocolName(e.target.value)}
                  className="rad-tech-input"
                  required
                />
                <div className="rad-quick-tags">
                  <button type="button" onClick={() => handleQuickProtocol('Standard PA Erect')}>Chest PA</button>
                  <button type="button" onClick={() => handleQuickProtocol('Whole Abdomen Transabdominal')}>USG Abdomen</button>
                  <button type="button" onClick={() => handleQuickProtocol('Spiral CECT 5mm Slices')}>CECT Scan</button>
                  <button type="button" onClick={() => handleQuickProtocol('Axial T1/T2/FLAIR + DWI')}>MRI Brain</button>
                </div>
              </div>

              <div className="rad-tech-field">
                <label>Exposure / Acquisition Parameters (kV, mAs, Slices)</label>
                <input
                  type="text"
                  placeholder="e.g. 120 kVp, 200 mAs, 0.625mm collimation"
                  value={kvMas}
                  onChange={e => setKvMas(e.target.value)}
                  className="rad-tech-input"
                />
              </div>

              <div className="rad-tech-field">
                <label>Contrast Administration (Agent, Volume, Reaction)</label>
                <textarea
                  rows="2"
                  placeholder="e.g. 70 ml Iohexol 350 mgI/ml IV via power injector @ 3 ml/s. No adverse reaction."
                  value={contrastDetails}
                  onChange={e => setContrastDetails(e.target.value)}
                  className="rad-tech-textarea"
                />
              </div>
            </div>

            {/* Right Column: Measurements & Notes */}
            <div className="rad-tech-col">
              <div className="rad-tech-field">
                <label>Technician Anatomical Measurements</label>
                <textarea
                  rows="3"
                  placeholder="e.g. Right Kidney: 10.4 cm, Left Kidney: 10.2 cm, Spleen: 9.8 cm"
                  value={measurements}
                  onChange={e => setMeasurements(e.target.value)}
                  className="rad-tech-textarea font-mono"
                />
                <div className="rad-quick-tags">
                  <button type="button" onClick={() => handleQuickMeasurement('Right Kidney', '10.5')}>Rt Kidney (10.5)</button>
                  <button type="button" onClick={() => handleQuickMeasurement('Left Kidney', '10.2')}>Lt Kidney (10.2)</button>
                  <button type="button" onClick={() => handleQuickMeasurement('Spleen', '9.5')}>Spleen (9.5)</button>
                  <button type="button" onClick={() => handleQuickMeasurement('CBD Diameter', '0.4')}>CBD (0.4)</button>
                </div>
              </div>

              <div className="rad-tech-field">
                <label>Technician Notes & Patient Cooperation</label>
                <textarea
                  rows="3"
                  placeholder="e.g. Adequate breath-hold achieved. Minimal motion artifact. Contrast tolerated well."
                  value={technicianNotes}
                  onChange={e => setTechnicianNotes(e.target.value)}
                  className="rad-tech-textarea"
                />
              </div>
            </div>
          </div>

          <div className="rad-tech-field" style={{ marginTop: '12px' }}>
            <label>Handover Remarks to Radiologist</label>
            <input
              type="text"
              placeholder="e.g. Images uploaded to PACS, contrast phase complete. Ready for doctor reporting."
              value={handoverRemarks}
              onChange={e => setHandoverRemarks(e.target.value)}
              className="rad-tech-input"
            />
          </div>

          {/* Footer Actions */}
          <div className="rad-tech-footer">
            <div className="rad-tech-footer-hint">
              ⌨️ Press <strong>Enter</strong> on form submit or click <strong>Hand Over to Doctor</strong> to update study status to <strong>In Review</strong>.
            </div>
            <div className="rad-tech-footer-btns">
              <button type="button" className="rad-btn-cancel" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="rad-btn-handover" disabled={submitting}>
                {submitting ? 'Submitting Handover...' : '🚀 Complete & Hand Over to Doctor'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
